import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { DefaultFilters, Filter } from '@mimir/common/filters/Filters';
import type { ActionContext } from 'vuex';
import type { StoreType } from '../store/state';
import { MEDIA_FILTER, MediaTypes, filters, setRadioFilter, setSelectedMediaTypes, NonMediaTypes, type SearchCategoryUI, ALL_FILTER } from '../globals/filter';
import { getSearchCategoryAlias, isSearchCategory, type SearchCategory, type SearchCategoryAlias } from '@mimir/common/dto/searchModels';
import type { RawLocation, Route } from 'vue-router/types/router';
import { getFiltersFromSearchString } from '@mimir/common/filters/filter-util';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { MediaAvailabilityStatus } from '@mimir/common/dto/mediaDtos';
import { getI18n } from '@mimir/common/i18n/initializeI18n';
import { canCreateFolder, canCreatePerson, canManageLiveRecording } from '@mimir/common/util/security';
import { assertUnreachable } from '@mimir/common/util/general';
import { FOLDER_DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import { mdi } from '../compat/vueCompat';
import { BM_QUERY_PARAM } from '@mimir/common/dto/userSettingsModels';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import type { SearchState } from '../model/searchState';


export type SetDateFilterFieldPayload = { key: string, label: string, mdf: MetaDataSchema | undefined };

export function setDateFilterField(context: ActionContext<any, any>, payload: SetDateFilterFieldPayload | undefined) {
  context.commit('setDateFilterField', payload);
}

export function clearTypeFilter(filterRemoved: Filter<any>, searchState: SearchState) {
  // Type filter values are combined into a single chip
  filterRemoved.clearValue();
  // When item filter type is removed, set the radio filter to all.
  // if there is no media item types are selected, set all media item types and show them as disabled.
  searchState.ops.setAllTypesFilter(true);
}

export function updateMediaTypeFilters(types: SearchCategoryAlias[]) {
  const categories: SearchCategoryAlias[] = [];
  types.forEach((type) => {
    if (isMediaType(type)) {
      categories.push(type);
    }
  });
  if (!categories.length) return;
  setSelectedMediaTypes(categories);
}

export function isMediaType(type: any): type is typeof MediaTypes[number] {
  return MediaTypes.includes(type);
}

export function isNonMediaType(type: any): type is typeof NonMediaTypes[number] {
  return NonMediaTypes.includes(type);
}

export function hasOnlyNonMediaTypes(selected: SearchCategoryAlias[]): boolean {
  return !selected.some(category => isMediaType(category));
}

// Get types directly from route query, useful for bookmarks because state filter object is not loaded yet.
export function getTypesFromSearchString(route: Route): SearchCategoryAlias[] {
  const searchString = Array.isArray(route.query.searchString) ? route.query.searchString[0] : route.query.searchString;
  const searchTerms = searchString ? getFiltersFromSearchString(searchString) : [];

  const formIdFilter = searchTerms?.find(term => term.startsWith('type:'));
  const searchCategories = formIdFilter ? formIdFilter.replace('type:', '').split(',') : [];

  const searchCategoryAliases = new Set<SearchCategoryAlias>();
  searchCategories.forEach(c => {
    if (isSearchCategory(c)) {
      searchCategoryAliases.add(getSearchCategoryAlias(c));
    }
  });
  return [...searchCategoryAliases.values()];
}

export function hasBothMediaAndOtherTypes(selected: SearchCategoryAlias[]): boolean {
  const hasMediaTypes = selected.some(isMediaType);
  const hasOtherTypes = selected.some(category => !isMediaType(category));
  return hasMediaTypes && hasOtherTypes;
}

export const sanitizeFilterForFolderType = (newRadioFilter: SearchCategoryUI, oldRadioFilter: SearchCategoryUI | null, store: StoreType) => {
  const showFolderMetadata = store.getters.featureFlags.showFolderMetadataEditor as boolean;
  if (!showFolderMetadata) return;
  const defaultFilters = store.getters.filters.defaultFilters as DefaultFilters;
  if (newRadioFilter === 'folder') {
    defaultFilters['filter:formId'].clearValue();
    defaultFilters['filter:formId'].addValue(FOLDER_DEFAULT_MDF_ID);
  } else if (oldRadioFilter === 'folder') {
    defaultFilters['filter:formId'].clearValue();
  }
};

export const getUploadIcon = (type: SearchCategory | SearchCategoryAlias) => {
  switch (type) {
    case ItemType.IMAGE:
    case ItemType.AUDIO:
    case ItemType.VIDEO:
    case ItemType.FILE: return mdi('tray-arrow-up');
    case ItemType.PERSON: return 'add';
    case ItemType.FOLDER: return 'add';
    case MediaAvailabilityStatus.STREAMING: return 'add';
    default: return '';
  }
};

export const getTooltip = (type: SearchCategory | SearchCategoryAlias, _isSelected: boolean) => {
  const prefix = getI18n().t('SearchMenuItem.Show');
  switch (type) {
    case ItemType.VIDEO: return getI18n().t('SearchMenuItem.Videos', {prefix});
    case ItemType.IMAGE: return getI18n().t('SearchMenuItem.Images', {prefix});
    case ItemType.PERSON: return getI18n().t('SearchMenuItem.Persons', {prefix});
    case ItemType.AUDIO: return getI18n().t('SearchMenuItem.Audio', {prefix});
    case ItemType.FILE: return getI18n().t('SearchMenuItem.File', {prefix});
    case 'sequence':
    case ItemType.CLIP_LIST:
    case ItemType.TIMELINE: return getI18n().t('SearchMenuItem.Sequences', {prefix});
    case ItemType.FOLDER: return getI18n().t('SearchMenuItem.Folders', {prefix});
    case MediaAvailabilityStatus.STREAMING: return getI18n().t('SearchMenuItem.Streaming', {prefix});
    default: return '';
  }
};

/**
 * Reset to 0, then apply search results to counts.
 */
export const typeCountsByType = (searchState: SearchState, list: SearchCategory[]): Record<string, number> => {
  const typeCount = searchState.reactive.typeCount;
  const mappedTypeCount = list
    .reduce<Record<string, number>>((map, cur) => {
      map[cur] = 0;
      return map;
    }, {});

  if (typeCount) {
    for (const type of typeCount) {
      mappedTypeCount[type.key] = type.doc_count;
    }
  }
  return mappedTypeCount;
};

export const hasPlusButton = (itemType: SearchCategoryAlias, canUpdateFolder: boolean, permissions: string[], doesHaveUploadPermission: boolean) => {
  return () => {
    switch (itemType) {
      case ItemType.PERSON: return canCreatePerson(permissions);
      case ItemType.FOLDER: return canCreateFolder(permissions) && canUpdateFolder;
      case 'sequence': return false;
      case MediaAvailabilityStatus.STREAMING: return canManageLiveRecording(permissions) && canUpdateFolder;
      case ItemType.VIDEO:
      case ItemType.AUDIO:
      case ItemType.IMAGE:
      case ItemType.FILE:
        return doesHaveUploadPermission && canUpdateFolder;
      default: assertUnreachable(itemType);
    }
  };
};

export function updateRadioFilterBasedOnSearchCategory(searchCategory: SearchCategoryAlias[], isAllFilter: boolean) {
  if (isAllFilter && filters.radioFilter !== ALL_FILTER) {
    setRadioFilter(ALL_FILTER);
    return;
  }

  if (filters.radioFilter === ALL_FILTER && !searchCategory.length) {
    return;
  }
  // If both media and non-media types are selected, set radio filter to all.
  if (searchCategory.length > 1 && hasBothMediaAndOtherTypes(searchCategory)) {
    if (filters.radioFilter !== ALL_FILTER as string) {
      setRadioFilter(ALL_FILTER);
    }
    return;
  }
  // Set non-media radio filter.
  if (searchCategory.length === 1 && isNonMediaType(searchCategory[0])) {
    if (filters.radioFilter !== searchCategory[0]) {
      setRadioFilter(searchCategory[0]);
    }
    return;
  }
  // If only multiple non-media types are selected, set radio filter to all.
  if (searchCategory.length > 1 && hasOnlyNonMediaTypes(searchCategory)) {
    if (filters.radioFilter !== ALL_FILTER) {
      setRadioFilter(ALL_FILTER);
    }
    return;
  }

  // If only media types are selected, set radio filter to media.
  if (searchCategory.length !== 0 && isMediaType(searchCategory[0])) {
    if (filters.radioFilter !== MEDIA_FILTER) {
      setRadioFilter(MEDIA_FILTER);
    }
    return;
  }
  // If no type selected, set radio filter to all. Though initially for no type, radio filter is set to media.
  // Here we are setting it to all.
  // If there is an existing bookmark where File name is present and no type is selected,
  // we should show the files in the search results. Since file is not part of media type we need to set radio filter to all.
  if (searchCategory.length === 0 && filters.radioFilter !== ALL_FILTER) {
    setRadioFilter(ALL_FILTER);
  }
}

export function shouldUseDefaultFilterOrBookmark(route: Route): boolean {
  if (route.name !== 'searchItem' && route.name !== 'sourceBrowser') {
    return false;
  }

  const searchCategory = getTypesFromSearchString(route);
  const isAllTypes = isAllFilterType(route, searchCategory);
  // Should not apply default filter if allTypes filter is present.
  if (isAllTypes) {
    return false;
  }

  // Should not apply default filter if any type filter is present.
  if (searchCategory.length) {
    return false;
  }

  return true;
}

/**
 * Applies default type filters for initial search navigation when no type filter is present.
 * Returns a RawLocation to redirect to, or void if no redirect is needed.
 */
export function getDefaultFilterRedirectIfNeeded(to: Route, store: StoreType): RawLocation | void {
  // Only apply to search routes
  if (to.name !== 'searchItem' && to.name !== 'folderView' && to.name !== 'sourceBrowser') {
    return;
  }

  const searchCategory = getTypesFromSearchString(to);
  const isAllTypes = isAllFilterType(to, searchCategory);

  // If there's already a type filter or allTypes parameter, no redirect needed
  if (searchCategory.length > 0 || isAllTypes) {
    return;
  }

  const tenantConfig = store.getters.tenantConfig as TenantConfigDto;
  const isDefaultAll = tenantConfig.showAllItemTypesInDefaultSearch ?? false;

  if (isDefaultAll) {
    // Redirect to add allTypes parameter
    return {
      name: to.name ?? 'searchItem',
      query: { ...to.query, allTypes: 'true' },
    };
  } else {
    // Redirect to add media type filters to search string
    const defaultFilters = store.getters.filters.defaultFilters as DefaultFilters;
    const mediaTypes = filters.selectedMediaTypes;

    mediaTypes.forEach((type) => {
      if (type === 'sequence') {
        defaultFilters['filter:itemType'].addValue('clipList');
        defaultFilters['filter:itemType'].addValue('timeline');
      } else {
        defaultFilters['filter:itemType'].addValue(type);
      }
    });

    const typeFilterValue = defaultFilters['filter:itemType'].serializedValue;
    const existingSearchString = to.query.searchString;
    const searchString = existingSearchString
      ? typeFilterValue + ' ' + existingSearchString
      : typeFilterValue;

    return {
      name: to.name ?? 'searchItem',
      query: { ...to.query, searchString },
    };
  }
}

function isAllFilterType(to: Route, typeFilters: SearchCategoryAlias[]): boolean {
  const bmQueryParam = to.query[BM_QUERY_PARAM];
  return  to.query.allTypes === 'true' || !!(typeFilters.length === 0 && bmQueryParam);
}

